// Farbe als Einstellung (docs/gestaltung.md, „Farbe"; Mockup
// docs/mockups/farbe-modern.html, Tim 03.10.2026: „Setzen wir so um inkl.
// der Vorlagen. Dann kann man es so anpassen wie man es mag.").
//
// EIN Satz Schalter, vier Vorlagen darüber. Die Vorlage wird nicht
// gespeichert -- sie ergibt sich aus der Stellung der Schalter; wer einen
// bewegt, hat „Eigene". Reine Logik ohne Laufzeit-Importe, damit die
// Tests ohne Browser laufen (farbe.test.mjs).
//
// Was sich NIE ändert: Grün, Gelb, Rot heißen „ok, Handgriff, kaputt".
// Diese Farbtöne gibt es hier darum nicht zur Auswahl.

/** Wählbare Farbtöne für Bereiche, im Farbkreis geordnet -- so stehen sie
 *  auch in der Farbkarte, ähnliche nebeneinander. Die Werte (Helligkeit je
 *  Thema) stehen in tokens-light.css / tokens-dark.css als --ton-<key>.
 *
 *  Sechzehn Töne seit 13.10.2026 (Tim: „Ich finde schlecht was
 *  passendes"). Von den elf davor lagen im Dunkeln 16 von 55 Paaren unter
 *  Abstand 15 -- Rosenholz, Terrakotta, Sand und Salbei waren praktisch
 *  eine Familie, Petrol, Blau und Indigo eine zweite. Jetzt trennt nicht
 *  nur der Farbton, sondern auch Helligkeit und Kraft: Terrakotta ist
 *  dunkel, Sand hell, Himmel hell, Petrol dunkel.
 *
 *  Rosenholz, Terrakotta, Sand, Oliv, Salbei und Blau sind die SCHWÄCHEREN
 *  Töne aus den Familien der Zustandsfarben. Kräftig bleiben Rot, Gelb,
 *  Grün und Blau dem Zustand vorbehalten (Kritisch, Warnung, ok, Info).
 *  Den Abstand jedes Tons zu ihnen und zum Akzent prüft
 *  farbe-toene.test.mjs -- wer einen Ton ergänzt oder verstellt, sieht
 *  dort, ob er noch trägt. */
export const TON_KEYS = [
  "rosenholz",
  "terrakotta",
  "sand",
  "oliv",
  "limette",
  "salbei",
  "tuerkis",
  "himmel",
  "petrol",
  "blau",
  "indigo",
  "violett",
  "orchidee",
  "beere",
  "stein",
  "graphit",
] as const;
export type TonKey = (typeof TON_KEYS)[number];

/** Die Bereiche = SectionId aus nav-sections.ts, in deren Reihenfolge
 *  (seit 16.10.2026 Claude dazu, seit 18.10.2026 Werkstatt, seit
 *  28.10.2026 Argus). */
export const BEREICHE = ["home", "werkstatt", "infra", "sec", "argus", "system", "daten", "claude"] as const;
export type BereichId = (typeof BEREICHE)[number];

export type AkzentEinsatz = "dezent" | "kraeftig";
export type DiagrammFarben = "grau" | "bereich" | "bunt";
export type ZustandZeigen = "auffaellig" | "alle";

export interface FarbSchalter {
  /** kräftig: aktiver Eintrag getönt, Hauptaktion gefüllt, Zähl-Pille getönt. */
  akzent: AkzentEinsatz;
  /** Bereichsfarbe als Ort: Tab-Linie, Baum und Register tragen die Farbe
   *  des Bereichs statt des Akzents. */
  ort: boolean;
  /** Symbole in der Farbe ihres Bereichs, je Ort einzeln (Tim 16.10.2026:
   *  „die einzelnen Kategorien einstellbar machen. Seitenleiste, Baum,
   *  Tabs"). Aus: grau im Dunkeln, dunkel im Hellen. */
  symbole: SymbolFarben;
  diagramme: DiagrammFarben;
  /** alle: auch „ok" trägt Grün. auffaellig: Normales bleibt leise. */
  zustand: ZustandZeigen;
  /** 0-100: wie stark Sektionsköpfe und Karten die Bereichsfarbe annehmen. */
  toenung: number;
  /** 60-130: Sättigung der Bereichsfarben in Prozent. */
  kraft: number;
}

/** Wo Symbole ihre Bereichsfarbe tragen können. seiten = Übersichten,
 *  Zonen-Köpfe des Dashboards, Kacheln. */
export const SYMBOL_ORTE = ["leiste", "baum", "tabs", "seiten"] as const;
export type SymbolOrt = (typeof SYMBOL_ORTE)[number];
export type SymbolFarben = Record<SymbolOrt, boolean>;
const ueberall = (an: boolean): SymbolFarben => ({ leiste: an, baum: an, tabs: an, seiten: an });

export interface Farbe extends FarbSchalter {
  bereiche: Record<BereichId, TonKey>;
}

export const VORLAGE_IDS = ["ruhig", "ort", "lebendig", "kraeftig"] as const;
export type VorlageId = (typeof VORLAGE_IDS)[number];

/** Die vier Stände des Mockups (01-04). */
export const VORLAGEN: Record<VorlageId, FarbSchalter> = {
  ruhig: { akzent: "dezent", ort: false, symbole: ueberall(false), diagramme: "grau", zustand: "auffaellig", toenung: 0, kraft: 100 },
  ort: { akzent: "dezent", ort: true, symbole: ueberall(false), diagramme: "bereich", zustand: "auffaellig", toenung: 0, kraft: 100 },
  lebendig: { akzent: "kraeftig", ort: true, symbole: ueberall(true), diagramme: "bunt", zustand: "auffaellig", toenung: 0, kraft: 100 },
  kraeftig: { akzent: "kraeftig", ort: true, symbole: ueberall(true), diagramme: "bunt", zustand: "alle", toenung: 60, kraft: 115 },
};

/** Werkstatt (18.10.2026) ist Sand: einer der schwächeren Töne, warm wie
 *  die Werkbank, und unter den freien der mit dem größten Abstand zu den
 *  übrigen sechs (dunkel 16,1, hell 15,7 -- beide Male zu Türkis). */
/** Argus (28.10.2026) ist Oliv, Tims Wahl im Mockup (docs/mockups/
 *  argus-modul.html). Damit es im Standard-Satz trägt, ist Oliv im Hellen
 *  zwei Punkte heller als vorher (50 % → 52 %): zu Terrakotta (Claude) lag
 *  es bei 11,6, jetzt 12,7; dunkel 12,5. */
export const BEREICHE_STANDARD: Record<BereichId, TonKey> = {
  home: "beere",
  werkstatt: "sand",
  infra: "blau",
  sec: "violett",
  argus: "oliv",
  system: "graphit",
  daten: "tuerkis",
  claude: "terrakotta",
};

/** Fertige Sätze für alle Bereiche: wer nicht siebenmal einzeln wählen
 *  will, nimmt einen Vorschlag und stellt danach um. Gerechnet, nicht nur
 *  nach Geschmack: die Töne eines Satzes liegen in beiden Themen
 *  mindestens 12 auseinander (farbe-toene.test.mjs). System ist überall
 *  Graphit -- das Werkzeug bleibt grau, die Orte bekommen Farbe.
 *
 *  Mit der Werkstatt (18.10.2026) ging „Frisch" nicht mehr auf: kein
 *  freier Ton lag zu allen sechs weit genug. Zuhause wurde dort Beere statt
 *  Rosenholz (der nächste Farbton), die Werkstatt Orchidee.
 *
 *  Mit Argus (28.10.2026) ging „Gedeckt" nicht mehr auf: kein freier Ton
 *  hielt zu allen sieben 12 Abstand. Sicherheit ist dort Beere statt
 *  Rosenholz (wieder der nächste Farbton), Argus Terrakotta. Warm: Türkis,
 *  Frisch: Terrakotta, Kühl: Beere. */
export const SATZ_IDS = ["standard", "warm", "frisch", "kuehl", "gedeckt"] as const;
export type SatzId = (typeof SATZ_IDS)[number];

export const SAETZE: Record<SatzId, Record<BereichId, TonKey>> = {
  standard: BEREICHE_STANDARD,
  warm: { home: "terrakotta", werkstatt: "beere", infra: "blau", sec: "violett", argus: "tuerkis", system: "graphit", daten: "sand", claude: "orchidee" },
  frisch: { home: "beere", werkstatt: "orchidee", infra: "himmel", sec: "indigo", argus: "terrakotta", system: "graphit", daten: "limette", claude: "oliv" },
  kuehl: { home: "salbei", werkstatt: "sand", infra: "blau", sec: "violett", argus: "beere", system: "graphit", daten: "tuerkis", claude: "terrakotta" },
  gedeckt: { home: "sand", werkstatt: "tuerkis", infra: "blau", sec: "beere", argus: "terrakotta", system: "graphit", daten: "oliv", claude: "violett" },
};

/** Welcher Vorschlag entspricht der Wahl? null = selbst zusammengestellt. */
export function passenderSatz(bereiche: Record<BereichId, TonKey>): SatzId | null {
  return SATZ_IDS.find((id) => BEREICHE.every((b) => SAETZE[id][b] === bereiche[b])) ?? null;
}

/** Standard = Stand 03 „Lebendig" (Tims Wahl 03.10.2026). */
export const FARBE_STANDARD: Farbe = { ...VORLAGEN.lebendig, bereiche: { ...BEREICHE_STANDARD } };

const SCHALTER: (keyof FarbSchalter)[] = ["akzent", "ort", "symbole", "diagramme", "zustand", "toenung", "kraft"];

function gleich(a: FarbSchalter, b: FarbSchalter, k: keyof FarbSchalter): boolean {
  if (k === "symbole") return SYMBOL_ORTE.every((o) => a.symbole[o] === b.symbole[o]);
  return a[k] === b[k];
}

function ganz(v: unknown, min: number, max: number, def: number): number {
  if (typeof v !== "number" || !isFinite(v)) return def;
  return Math.min(max, Math.max(min, Math.round(v)));
}

/** Farbige Symbole je Ort. Bis 16.10.2026 gab es EINEN Schalter
 *  `kacheln` -- ein so gemerkter Wert gilt für alle Orte. */
function symboleAus(r: Record<string, unknown>, d: SymbolFarben): SymbolFarben {
  const s = (r.symbole && typeof r.symbole === "object" ? r.symbole : {}) as Record<string, unknown>;
  const alt = typeof r.kacheln === "boolean" ? r.kacheln : undefined;
  const aus = { ...d };
  for (const o of SYMBOL_ORTE) aus[o] = typeof s[o] === "boolean" ? (s[o] as boolean) : (alt ?? d[o]);
  return aus;
}

/** Ein Bereich ohne gemerkten Ton -- neu dazugekommen wie die Werkstatt
 *  (18.10.2026), gemerkt war nur der Rest. Passt der Rest zu einem
 *  Vorschlag, kommt der Ton aus diesem; sonst der Standard, außer ein
 *  anderer Bereich trägt ihn schon (wer „Warm" gewählt hatte, hat Daten in
 *  Sand) -- dann der erste freie. Zwei Bereiche in einem Ton sähe man
 *  nicht auseinander. */
function fuelleNeueBereiche(bereiche: Record<BereichId, TonKey>, gemerkt: readonly BereichId[]): void {
  if (!gemerkt.length || gemerkt.length === BEREICHE.length) return;
  const satz = SATZ_IDS.find((id) => gemerkt.every((g) => SAETZE[id][g] === bereiche[g]));
  for (const id of BEREICHE) {
    if (gemerkt.includes(id)) continue;
    if (satz) {
      bereiche[id] = SAETZE[satz][id];
      continue;
    }
    const vergeben = new Set(BEREICHE.filter((x) => x !== id).map((x) => bereiche[x]));
    if (vergeben.has(bereiche[id])) bereiche[id] = TON_KEYS.find((k) => !vergeben.has(k)) ?? bereiche[id];
  }
}

/** Liest einen gespeicherten Wert. Unbekanntes fällt je Feld auf den
 *  Standard -- ein alter oder halber Eintrag darf die Seite nie brechen. */
export function normalisiereFarbe(raw: unknown): Farbe {
  const r = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const d = FARBE_STANDARD;
  const b = (r.bereiche && typeof r.bereiche === "object" ? r.bereiche : {}) as Record<string, unknown>;
  const bereiche = { ...BEREICHE_STANDARD };
  const gemerkt = BEREICHE.filter((id) => (TON_KEYS as readonly unknown[]).includes(b[id]));
  for (const id of gemerkt) bereiche[id] = b[id] as TonKey;
  fuelleNeueBereiche(bereiche, gemerkt);
  return {
    akzent: r.akzent === "dezent" || r.akzent === "kraeftig" ? r.akzent : d.akzent,
    ort: typeof r.ort === "boolean" ? r.ort : d.ort,
    symbole: symboleAus(r, d.symbole),
    diagramme: r.diagramme === "grau" || r.diagramme === "bereich" || r.diagramme === "bunt" ? r.diagramme : d.diagramme,
    zustand: r.zustand === "auffaellig" || r.zustand === "alle" ? r.zustand : d.zustand,
    toenung: ganz(r.toenung, 0, 100, d.toenung),
    kraft: ganz(r.kraft, 60, 130, d.kraft),
    bereiche,
  };
}

/** Welche Vorlage entspricht der Stellung der Schalter? null = Eigene.
 *  Die Farbe je Bereich zählt nicht mit: sie ist Geschmack, keine Stufe. */
export function passendeVorlage(f: FarbSchalter): VorlageId | null {
  return VORLAGE_IDS.find((id) => SCHALTER.every((k) => gleich(VORLAGEN[id], f, k))) ?? null;
}

/** Vorlage wählen: setzt alle Schalter, lässt die Farben der Bereiche. */
export function mitVorlage(f: Farbe, id: VorlageId): Farbe {
  return { ...VORLAGEN[id], symbole: { ...VORLAGEN[id].symbole }, bereiche: { ...f.bereiche } };
}

/** Akzentfarben zum Anklicken in der Karte „Farbe". Ein freier Farbton
 *  (0-360) bleibt daneben möglich. Violett wäre Kestra, Koralle n8n. */
export const AKZENT_TOENE = [
  { hue: 35, name: "Orange" },
  { hue: 12, name: "Koralle" },
  { hue: 340, name: "Pink" },
  { hue: 290, name: "Violett" },
  { hue: 250, name: "Blau" },
  { hue: 190, name: "Türkis" },
] as const;

/** Standard-Akzent (Marken-Orange). */
export const AKZENT_STANDARD = 35;

/** Fester Platz je Messgröße in der Diagramm-Palette (--reihe-N): die
 *  Farbe folgt der Größe, nicht der Seite -- Temperatur ist überall
 *  dieselbe. Bewusst ohne die Plätze 4, 6, 8 (Gelb, Grün, Rot): die läsen
 *  sich wie ein Zustand. Gilt bei „Diagramme: bunt"; sonst zeichnet das
 *  Diagramm Abstufungen einer Farbe. */
export const REIHE = {
  feuchte: 1,
  leistung: 1,
  frequenz: 1,
  bewegung: 1,
  temperatur: 2,
  druck: 3,
  co2: 5,
  iaq: 7,
  // Server-Seite (15.10.2026): Last und CPU, Speicher (Rechner, Dienst,
  // Datenbank), Schreibrate, Anfragen.
  auslastung: 1,
  speicher: 3,
  schreibrate: 5,
  anfragen: 7,
  // DNS-Seite (24.10.2026): gesperrte Anfragen neben „anfragen".
  gesperrt: 1,
  // Karte „Lüftung & Heizung" (25.10.2026): Außenluft kühl, Innenluft
  // daneben, Heizung warm (wie die Temperatur).
  lueftungAussen: 1,
  lueftungInnen: 3,
  heizung: 2,
} as const;

/** Reihenfolge ohne feste Zuordnung: erst die fünf, die kein Zustand
 *  sein könnten, dann der Rest. */
export const REIHEN_FOLGE = [1, 2, 3, 5, 7, 4, 6, 8] as const;

/** Tönung in Prozent für color-mix: Sektionskopf bis 9 %, Kartenfläche
 *  bis 4 % -- mehr würde wieder zum Farb-Nebel (Klar-Redesign 2026-07). */
export function toenungProzent(toenung: number): { kopf: number; grund: number } {
  const t = Math.min(100, Math.max(0, toenung)) / 100;
  return { kopf: Math.round(t * 90) / 10, grund: Math.round(t * 40) / 10 };
}
